// ABOUTME: Uploads a file into storage the way the server takes it (gql-api docs/frontend-files.md §4): a start, the bytes
// ABOUTME: into its resumable session, a completion — with the retries each answer allows, two transfers at a time.
import type { ApolloClient } from "@apollo/client"
import {
  FileState,
  FileUploadCompleteDocument,
  FileUploadDocument,
  FileUploadStartDocument,
  type AssetAttachmentFragment,
} from "@/graphql/main/gql/graphql"
import { serverErrorCode } from "@/graphql/client/errorMessage"
import { abortError, isCancelled, pause } from "./abortable"
import { SessionFailedError, SessionGoneError, sendToSession, type SessionTransport } from "./resumableSession"

/** Where an upload is, as its tile shows it. */
export type UploadPhase = "queued" | "sending" | "completing" | "waitingServer"

export interface UploadProgress {
  phase: UploadPhase
  /** Bytes in storage plus those of the chunk on their way. */
  sentBytes: number
}

/** The attempt the server knows the upload by: kept between tries, so another one goes on where this stopped. */
export interface UploadAttempt {
  /** Made once per picked file and kept through every retry: a start repeated with it answers the same upload. */
  clientUploadId: string
  /** Known once a start answered: another try reads the upload's state instead of starting again. */
  assetId: string | null
}

/**
 * Why an upload stopped: the server's code, or a key of the client's own text when storage broke off — and whether
 * another try (↻) may help. Neither carries the session: it is a key to the upload.
 */
export class UploadFailure extends Error {
  constructor(
    readonly code: string,
    readonly isRetryable: boolean,
  ) {
    super(code)
    this.name = "UploadFailure"
  }
}

/** Transfers into storage at once; the rest wait their turn ("в очереди"). */
export const MAX_TRANSFERS = 2
/** How often a completion is tried again while the server cannot take files (ErrFileUploadUnavailable). */
export const UNAVAILABLE_RETRY_MS = 30_000
/** How long a start, a read or a completion may go without an answer before it counts as none. */
export const ANSWER_TIMEOUT_MS = 60_000
/** The pauses before each further try of a start, a read or a completion that got no answer, or ErrSystemErrors: a
 * blip of the network does not use them all up at once. */
const REPEAT_PAUSES_MS = [1000, 2000]

let running = 0
const waiting: (() => void)[] = []

/**
 * A place among the transfers into storage. Taken before the start, so that files waiting their turn hold no room of
 * the person's on the server yet; released once the bytes are in, or the upload stopped.
 */
function takeTransfer(signal: AbortSignal): Promise<() => void> {
  return new Promise((resolve, reject) => {
    const grant = () => {
      signal.removeEventListener("abort", onAbort)
      running += 1
      let isReleased = false
      resolve(() => {
        if (isReleased) return
        isReleased = true
        running -= 1
        waiting.shift()?.()
      })
    }
    const onAbort = () => {
      const index = waiting.indexOf(grant)
      if (index >= 0) waiting.splice(index, 1)
      reject(abortError())
    }
    if (signal.aborted) {
      reject(abortError())
      return
    }
    if (running < MAX_TRANSFERS) {
      grant()
      return
    }
    waiting.push(grant)
    signal.addEventListener("abort", onAbort, { once: true })
  })
}

/** Test seam: no transfer runs, nobody waits. */
export function resetUploadQueueForTests(): void {
  running = 0
  waiting.length = 0
}

/** No answer came, or the server failed inside: the same request may still go through. */
const isWorthRepeating = (code: string | null) => code === null || code === "ErrSystemErrors"

/**
 * One try, bounded: a cancel rejects at once — the transfer place goes back, though the request may still run — and a
 * try with no answer for ANSWER_TIMEOUT_MS counts as none. The link bounds no mutation (graphql/client/timeoutLink.ts:
 * a cut write is a write unknown), but these are safe to ask again: a start is keyed by its clientUploadId, a read and
 * a completion by the asset (gql-api docs/frontend-files.md §4.1, §4.3). Unbounded, a hung one held its place for good.
 */
async function bounded<T>(request: () => Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) throw abortError()
  let stop = () => {}
  const cut = new Promise<never>((_resolve, reject) => {
    const onAbort = () => reject(abortError())
    const timer = setTimeout(() => reject(new TypeError("No answer")), ANSWER_TIMEOUT_MS)
    signal.addEventListener("abort", onAbort, { once: true })
    stop = () => {
      clearTimeout(timer)
      signal.removeEventListener("abort", onAbort)
    }
  })
  // Stopped once the race is over, the cut never rejects after it; this only keeps a late one from being unhandled.
  cut.catch(() => undefined)
  try {
    return await Promise.race([request(), cut])
  } finally {
    stop()
  }
}

/**
 * Runs `request`, asking again after a pause while no answer comes or the server fails inside, with the same arguments
 * — a start with the same id answers the same upload. Any other refusal comes back as it is.
 */
async function repeated<T>(request: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let tries = 0; ; tries += 1) {
    try {
      return await bounded(request, signal)
    } catch (error) {
      if (isCancelled(signal)) throw abortError()
      const repeatAfter = REPEAT_PAUSES_MS[tries]
      if (!isWorthRepeating(serverErrorCode(error)) || repeatAfter === undefined) throw error
      await pause(repeatAfter, signal)
    }
  }
}

interface Upload {
  session: string | null
  asset: AssetAttachmentFragment
}

/** Answers after which the same steps may still go through: files off for now, or bytes missing once more. */
const RETRYABLE_CODES: ReadonlySet<string> = new Set(["ErrFileUploadUnavailable", "ErrFileNotUploaded"])

const failureOf = (error: unknown): UploadFailure => {
  const code = serverErrorCode(error)
  // The rest — a type not taken, over the limit, no room left, access — would be refused the same way again.
  const isRetryable = isWorthRepeating(code) || RETRYABLE_CODES.has(code)
  return new UploadFailure(code ?? "errors.networkError", isRetryable)
}

export interface FileUploadOptions {
  client: ApolloClient
  file: File
  attempt: UploadAttempt
  signal: AbortSignal
  onProgress: (progress: UploadProgress) => void
  /** The attempt changed: the id a start answered, or a new attempt after the server or the session lost the old one. */
  onAttempt: (attempt: UploadAttempt) => void
  transport?: SessionTransport
}

/** One upload under way: its options, the attempt it is at, and the second chances it has used. */
interface Run extends FileUploadOptions {
  hasStartedOver: boolean
  hasSentAgain: boolean
  /** The session may hold bytes of this upload already: ask it first. */
  mayHoldBytes: boolean
}

/** The server or storage lost the upload: another try under a new id — once. */
function startOver(run: Run, error: unknown): "startOver" {
  if (run.hasStartedOver) throw failureOf(error)
  run.hasStartedOver = true
  run.attempt = { clientUploadId: crypto.randomUUID(), assetId: null }
  run.mayHoldBytes = false
  run.onAttempt(run.attempt)
  return "startOver"
}

/** The upload as the server holds it: read by its id when a start answered before, otherwise started. */
async function uploadOf(run: Run): Promise<Upload> {
  const { client, file, signal } = run
  const { assetId, clientUploadId } = run.attempt
  if (assetId !== null) {
    const { data } = await repeated(
      () => client.query({ query: FileUploadDocument, variables: { assetId }, fetchPolicy: "no-cache" }),
      signal,
    )
    if (!data) throw new UploadFailure("errors.networkError", true)
    return data.fileUpload
  }
  const { data } = await repeated(
    () =>
      client.mutate({
        mutation: FileUploadStartDocument,
        variables: { name: file.name, sizeBytes: file.size, clientUploadId },
        // The answer carries the session, a key to the upload: it stays out of the cache.
        fetchPolicy: "no-cache",
      }),
    signal,
  )
  if (!data) throw new UploadFailure("errors.networkError", true)
  run.attempt = { clientUploadId, assetId: data.fileUploadStart.asset.id }
  run.onAttempt(run.attempt)
  return data.fileUploadStart
}

/** What a stop on the bytes' way means: a start over, or the failure to show. */
function afterSendStopped(run: Run, error: unknown): "startOver" {
  if (isCancelled(run.signal)) throw abortError()
  if (error instanceof SessionGoneError || serverErrorCode(error) === "ErrAssetNotFound") return startOver(run, error)
  if (error instanceof SessionFailedError) {
    throw new UploadFailure(
      error.isRetryable ? "discussion.uploadBroke" : "discussion.uploadRefused",
      error.isRetryable,
    )
  }
  throw error instanceof UploadFailure ? error : failureOf(error)
}

/** The bytes into storage, holding a transfer place meanwhile. Answers the file itself when it is READY already. */
async function sendBytes(run: Run): Promise<AssetAttachmentFragment | "sent" | "startOver"> {
  const { file, signal, onProgress, transport } = run
  onProgress({ phase: "queued", sentBytes: 0 })
  const release = await takeTransfer(signal)
  try {
    const { session, asset } = await uploadOf(run)
    if (asset.file?.state === FileState.Ready) return asset
    // UPLOADING without a session is no success: the server's answer is not whole.
    if (session === null) throw new UploadFailure("errors.networkError", true)
    onProgress({ phase: "sending", sentBytes: 0 })
    await sendToSession({
      session,
      file,
      signal,
      resume: run.mayHoldBytes,
      onProgress: (sentBytes) => onProgress({ phase: "sending", sentBytes }),
      transport,
    })
    return "sent"
  } catch (error) {
    return afterSendStopped(run, error)
  } finally {
    release()
  }
}

/** What a refused completion means: wait for the server, send the bytes it lacks, start over — or the failure. */
async function afterCompleteRefused(run: Run, error: unknown): Promise<"again" | "sendAgain" | "startOver"> {
  if (isCancelled(run.signal)) throw abortError()
  const code = serverErrorCode(error)
  if (code === "ErrFileUploadUnavailable") {
    // Keep the upload: the server takes it once files are back.
    run.onProgress({ phase: "waitingServer", sentBytes: run.file.size })
    await pause(UNAVAILABLE_RETRY_MS, run.signal)
    return "again"
  }
  if (code === "ErrFileNotUploaded" && !run.hasSentAgain) {
    run.hasSentAgain = true
    run.mayHoldBytes = true
    return "sendAgain"
  }
  if (code === "ErrAssetNotFound") return startOver(run, error)
  // Asked again and again with no answer: the bytes are in, the completion is still to be asked (↻) — never them anew.
  if (!(error instanceof UploadFailure) && isWorthRepeating(code)) {
    throw new UploadFailure("discussion.uploadNotCompleted", true)
  }
  throw error instanceof UploadFailure ? error : failureOf(error)
}

/** The server checks the bytes and makes the file READY. Repeated with the same asset id, never with the bytes again. */
async function complete(run: Run, assetId: string): Promise<AssetAttachmentFragment | "sendAgain" | "startOver"> {
  for (;;) {
    run.onProgress({ phase: "completing", sentBytes: run.file.size })
    try {
      const { data } = await repeated(
        () =>
          run.client.mutate({ mutation: FileUploadCompleteDocument, variables: { assetId }, fetchPolicy: "no-cache" }),
        run.signal,
      )
      if (!data) throw new UploadFailure("errors.networkError", true)
      return data.fileUploadComplete.asset
    } catch (error) {
      const next = await afterCompleteRefused(run, error)
      if (next !== "again") return next
    }
  }
}

/**
 * Uploads the file and resolves with it READY, to be sent with a message. Rejects with UploadFailure — retryable or
 * not — or with an AbortError once cancelled. A known asset id goes on with that upload: its state is read, the bytes
 * the session lacks are sent, and it is completed. A gone upload or session starts over once under a new id; missing
 * bytes are sent once more.
 */
export async function uploadFile(options: FileUploadOptions): Promise<AssetAttachmentFragment> {
  const run: Run = {
    ...options,
    hasStartedOver: false,
    hasSentAgain: false,
    mayHoldBytes: options.attempt.assetId !== null,
  }
  for (;;) {
    const sent = await sendBytes(run)
    if (sent === "startOver") continue
    if (sent !== "sent") return sent
    const { assetId } = run.attempt
    if (assetId === null) throw new UploadFailure("errors.networkError", true)
    const completed = await complete(run, assetId)
    if (completed !== "startOver" && completed !== "sendAgain") return completed
  }
}
